import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useUserProfile } from '../masterLayout/MasterLayoutBody';
import {
  getPaginatedGlobalGroups,
  createGlobalGroup,
  updateGlobalGroup,
  deleteGlobalGroup,
} from '../api/globalGroupAPI';
import { useFormBuilder } from '../utils/useFormBuilder';
import { useTableFilters } from '../utils/useTableFilters';
import {
  loadGlobalClassFilterOptions,
  loadGlobalManufacturerFilterOptions,
} from '../utils/globalReferenceFilterOptions';
import { Icon } from '@iconify/react';

const PAGE_SIZE = 10;

const GlobalGroupLayer = () => {
  const userProfile = useUserProfile();
  const [list, setList] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({});
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [errors, setErrors] = useState({});
  const [refreshNonce, setRefreshNonce] = useState(0);

  const inputFields = useMemo(
    () => [
      { field: 'groupName', label: 'Group Name', type: 'text', size: 6, required: true },
      { field: 'globalClassId', label: 'Class', type: 'select', size: 6, api: 'global-class', displayField: 'className', required: true },
      { field: 'globalManufacturerId', label: 'Manufacturer', type: 'select', size: 6, api: 'global-manufacturer', displayField: 'manufacturer', required: true },
      { field: 'groupImage', label: 'Group Image', type: 'image', size: 12, directory: 'modelimages' },
    ],
    [],
  );

  const { renderField } = useFormBuilder(inputFields, formData, setFormData, errors);

  const tableFilters = useMemo(
    () => [
      {
        field: 'globalClassId',
        type: 'paginatedDropdown',
        label: 'Class',
        loadOptions: loadGlobalClassFilterOptions,
        isMulti: false,
      },
      {
        field: 'globalManufacturerId',
        type: 'paginatedDropdown',
        label: 'Manufacturer',
        loadOptions: loadGlobalManufacturerFilterOptions,
        isMulti: false,
      },
    ],
    [],
  );

  const isSearchActive = search.trim().length > 0;
  const totalPages = isSearchActive ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE));
  const startRecord = total === 0 ? 0 : isSearchActive ? 1 : (page - 1) * PAGE_SIZE + 1;
  const endRecord = total === 0 ? 0 : isSearchActive ? total : Math.min(page * PAGE_SIZE, total);

  const {
    appliedFilterParams,
    renderFilterControl,
    renderFilterBadges,
    resetFilters,
    applyFilters,
  } = useTableFilters(tableFilters);

  const manufacturerId = appliedFilterParams.globalManufacturerId;
  const classId = appliedFilterParams.globalClassId;

  const filtersQueryKey = useMemo(
    () => JSON.stringify({
      manufacturerId: manufacturerId ?? null,
      classId: classId ?? null,
    }),
    [classId, manufacturerId],
  );

  const queryKeyRef = useRef('');
  const pendingPageRef = useRef(null);

  useEffect(() => {
    let ignore = false;
    const currentQueryKey = `${search}::${filtersQueryKey}`;
    const queryChanged = queryKeyRef.current !== currentQueryKey;

    if (queryChanged) {
      queryKeyRef.current = currentQueryKey;
      setList([]);
      setTotal(0);
      if (page !== 1) {
        pendingPageRef.current = 1;
        setPage(1);
        return () => {
          ignore = true;
        };
      }
    }

    if (pendingPageRef.current !== null && page !== pendingPageRef.current) {
      return () => {
        ignore = true;
      };
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const resp = await getPaginatedGlobalGroups({
          page,
          pageSize: PAGE_SIZE,
          search,
          manufacturerId,
          classId,
        });

        if (ignore) return;

        setList(resp.data || []);
        setTotal(resp.total || 0);
        pendingPageRef.current = null;
      } catch (err) {
        if (!ignore) {
          console.error('Error fetching global groups:', err);
        }
      } finally {
        if (!ignore) {
          pendingPageRef.current = null;
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      ignore = true;
    };
  }, [classId, filtersQueryKey, manufacturerId, page, refreshNonce, search]);

  const validate = () => {
    const errs = {};
    if (!formData.groupName?.trim()) errs.groupName = 'Required';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;
    const clean = {};
    for (const k in formData)
      if (formData[k] !== null && typeof formData[k] !== 'object') clean[k] = formData[k];
    if (editingId) await updateGlobalGroup(editingId, clean);
    else await createGlobalGroup(clean);
    setShowModal(false);
    setEditingId(null);
    setFormData({});
    pendingPageRef.current = 1;
    setList([]);
    setTotal(0);
    setPage(1);
    setRefreshNonce((prev) => prev + 1);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete item?')) return;
    await deleteGlobalGroup(id);
    pendingPageRef.current = 1;
    setList([]);
    setTotal(0);
    setPage(1);
    setRefreshNonce((prev) => prev + 1);
  };

  const clearSearch = () => {
    setSearch('');
    setPage(1);
  };

  const handlePageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > totalPages || nextPage === page) {
      return;
    }
    setPage(nextPage);
  };

  const pageNumbers = useMemo(() => {
    const maxButtons = 5;
    if (totalPages <= maxButtons) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    if (page <= 3) {
      return [1, 2, 3, 4, 5];
    }

    if (page >= totalPages - 2) {
      return [totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages];
    }

    return [page - 2, page - 1, page, page + 1, page + 2];
  }, [page, totalPages]);

  const toolbarControlHeight = '42px';

  return (
    <div className='card h-100 p-0 rounded-0'>
      <div className='card-body p-0'>
        <div className='border-bottom bg-white px-3 py-3'>
          <div className='d-flex flex-wrap align-items-end justify-content-between gap-3'>
            <div className='d-flex flex-column gap-2 flex-grow-1'>
              <div className='d-flex align-items-center gap-2'>
                <Icon icon='mage:filter-2' className='icon text-xl line-height-1 text-secondary' />
                <h6 className='mb-0 text-lg fw-semibold'>Select Filters</h6>
              </div>
              <div className='d-flex flex-wrap align-items-end gap-3'>
                {tableFilters.map((config) => (
                  <React.Fragment key={config.field}>
                    {renderFilterControl(config, true, 'd-flex flex-column')}
                  </React.Fragment>
                ))}
                <div className='d-flex align-items-end gap-2'>
                  <button
                    id='reset-filters-btn'
                    className='btn btn-outline-secondary btn-sm px-16 py-12 radius-4'
                    onClick={resetFilters}
                  >
                    Reset
                  </button>
                  <button
                    id='apply-filters-btn'
                    className='btn btn-primary btn-sm px-16 py-12 radius-4'
                    onClick={applyFilters}
                  >
                    Apply
                  </button>
                </div>
              </div>
              <div className='d-flex flex-wrap gap-2 mt-1'>
                {renderFilterBadges()}
              </div>
            </div>

            <div className='d-flex align-items-end gap-2 ms-auto'>
              <div className='d-flex flex-column' style={{ minWidth: '260px' }}>
                <label className='form-label mb-1'>Search</label>
                <div className='input-group position-relative' style={{ height: toolbarControlHeight }}>
                  <input
                    type='text'
                    className='form-control form-control-sm'
                    placeholder='Search groups...'
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                    style={{ height: toolbarControlHeight, paddingRight: '2.5rem' }}
                  />
                  {search.trim() && (
                    <button
                      className='btn btn-link border-0 bg-transparent p-0 position-absolute top-50 end-0 translate-middle-y me-2 text-secondary'
                      type='button'
                      onClick={clearSearch}
                      aria-label='Clear search keyword'
                      title='Clear search'
                      style={{ width: '1.5rem', height: '1.5rem', boxShadow: 'none' }}
                    >
                      <Icon icon='mage:multiply' className='icon text-lg line-height-1' />
                    </button>
                  )}
                </div>
              </div>

              {userProfile?.privileges?.includes('global-group:create') && (
                <button
                  className='btn btn-primary text-sm btn-sm px-16 radius-4 d-flex align-items-center gap-2'
                  onClick={() => {
                    setFormData({});
                    setEditingId(null);
                    setShowModal(true);
                  }}
                  style={{ height: toolbarControlHeight }}
                >
                  <Icon icon='ic:baseline-plus' className='icon text-xl line-height-1' />
                  Add Group
                </button>
              )}
            </div>
          </div>
        </div>
        <div className='table-responsive scroll-sm' style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <table className='table bordered-table mb-0'>
            <thead><tr><th>Manufacturer</th><th>Name</th><th>Class</th><th>Actions</th></tr></thead>
            <tbody>
              {list.map((item) => (
                <tr key={item.id}>
                  <td>{item.globalManufacturer?.manufacturer}</td>
                  <td><strong>{item.groupName}</strong></td>
                  <td>{item.globalClass?.className}</td>
                  <td>
                    <div className='d-flex gap-2'>
                      {userProfile?.privileges?.includes('global-group:update') && (
                        <button className='w-32-px h-32-px bg-success-focus text-success-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => { setFormData(item); setEditingId(item.id); setShowModal(true); }}>
                          <Icon icon='ic:baseline-edit' className='icon text-xl line-height-1' />
                        </button>
                      )}
                      {userProfile?.privileges?.includes('global-group:delete') && (
                        <button className='w-32-px h-32-px bg-danger-focus text-danger-main rounded-circle d-inline-flex align-items-center justify-content-center' onClick={() => handleDelete(item.id)}>
                          <Icon icon='ic:baseline-delete-outline' className='icon text-xl line-height-1' />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
              {!loading && list.length === 0 && (
                <tr>
                  <td colSpan={4} className='text-center py-4 text-muted'>
                    No groups found
                  </td>
                </tr>
              )}
              {loading && (
                <tr>
                  <td colSpan={4} className='text-center py-2'>
                    <span className='spinner-border spinner-border-sm' />
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='d-flex justify-content-between align-items-center p-3 border-top'>
          <div className='text-muted'>
            Showing {startRecord} to {endRecord} of {total} records
          </div>
          {!isSearchActive && (
            <nav>
              <ul className='pagination mb-0'>
                <li className={`page-item ${page <= 1 ? 'disabled' : ''}`}>
                  <button className='page-link text-md' onClick={() => handlePageChange(page - 1)} disabled={page <= 1}>
                    Previous
                  </button>
                </li>
                {pageNumbers.map((pageNumber) => (
                  <li key={pageNumber} className={`page-item ${pageNumber === page ? 'active' : ''}`}>
                    <button className='page-link text-md' onClick={() => handlePageChange(pageNumber)}>
                      {pageNumber}
                    </button>
                  </li>
                ))}
                <li className={`page-item ${page >= totalPages ? 'disabled' : ''}`}>
                  <button className='page-link text-md' onClick={() => handlePageChange(page + 1)} disabled={page >= totalPages}>
                    Next
                  </button>
                </li>
              </ul>
            </nav>
          )}
        </div>
      </div>
      {showModal && (
        <div className='modal show d-block' tabIndex='-1'>
          <div className='modal-dialog modal-lg'><div className='modal-content'>
            <div className='modal-header'><h5 className='modal-title'>{editingId ? 'Edit Group' : 'New Group'}</h5><button className='btn-close' onClick={() => setShowModal(false)} /></div>
            <div className='modal-body'><div className='row g-3'>{inputFields.map(renderField)}</div></div>
            <div className='modal-footer'><button className='btn btn-secondary' onClick={() => setShowModal(false)}>Close</button><button className='btn btn-primary' onClick={handleSave}>Save</button></div>
          </div></div>
        </div>
      )}
    </div>
  );
};

export default GlobalGroupLayer;
